<template>
  <div class="fmp-device">
    <!-- Facebook Header bar -->
    <div class="fmp-header-bar">
      <div class="fmp-search-box fmp-clickable" @click="openFacebookPage" :title="$t('dash.presenceManagement.facebook.openPage')">
        <i class="material-icons fmp-search-icon">search</i>
        <span class="fmp-search-text">{{ name || $t('dash.presenceManagement.facebook.searchPlaceholder') }}</span>
      </div>
      <i class="material-icons fmp-header-icon" @click="sharePage" :title="$t('dash.presenceManagement.mockup.share')">share</i>
    </div>

    <!-- Phone screen content -->
    <div class="fmp-screen">

      <!-- Cover Photo Banner -->
      <div 
        class="fmp-cover fmp-clickable"
        :style="coverImageUrl ? `background-image: url('${coverImageUrl}')` : ''"
        @click="openFacebookPage"
        :title="$t('dash.presenceManagement.facebook.openPage')"
      >
        <div v-if="!coverImageUrl" class="fmp-cover-placeholder">
          <i class="material-icons">landscape</i>
        </div>
      </div>

      <!-- Page Info Section -->
      <div class="fmp-profile-info">
        <!-- Avatar overlapping cover -->
        <div class="fmp-avatar-wrapper fmp-clickable" @click="openFacebookPage" :title="$t('dash.presenceManagement.facebook.openPage')">
          <div 
            class="fmp-avatar"
            :style="imageUrl ? `background-image: url('${imageUrl}')` : ''"
          >
            <i v-if="!imageUrl" class="material-icons fmp-avatar-placeholder">storefront</i>
          </div>
        </div>

        <div class="fmp-identity">
          <h4 class="fmp-page-name fmp-clickable" @click="openFacebookPage" :title="$t('dash.presenceManagement.facebook.openPage')">
            {{ name || $t('dash.presenceManagement.facebook.defaultPageName') }}
          </h4>
          <p class="fmp-page-category">{{ categoryName || $t('dash.presenceManagement.facebook.defaultCategory') }}</p>
        </div>

        <!-- Details List -->
        <div class="fmp-details">
          <div class="fmp-detail-item" v-if="address">
            <i class="material-icons">place</i>
            <span>{{ address }}</span>
          </div>

          <div class="fmp-detail-item fmp-detail-item--clickable" v-if="phone" @click="callPhone">
            <i class="material-icons">phone</i>
            <span class="fmp-link">{{ phone }}</span>
          </div>

          <div class="fmp-detail-item fmp-detail-item--clickable" v-if="website" @click="openWebsite">
            <i class="material-icons">language</i>
            <span class="fmp-link">{{ cleanWebsite }}</span>
          </div>

          <div class="fmp-detail-item">
            <i class="material-icons">schedule</i>
            <span class="fmp-status-open">{{ $t('dash.presenceManagement.mockup.currentlyOpen') }}</span>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'facebook-mockup-preview',

  props: {
    name: { type: String, default: '' },
    address: { type: String, default: '' },
    phone: { type: String, default: '' },
    website: { type: String, default: '' },
    categoryName: { type: String, default: '' },
    imageUrl: { type: String, default: '' },
    coverImageUrl: { type: String, default: '' },
    pageUri: { type: String, default: '' },
    additionalPhotos: { type: Array, default: () => [] }
  },

  computed: {
    cleanWebsite() {
      if (!this.website) return ''
      return this.website.replace(/^https?:\/\//i, '').replace(/\/$/, '')
    },

    targetPageUrl() {
      if (this.pageUri) {
        return this.pageUri.startsWith('http') ? this.pageUri : `https://${this.pageUri}`
      }
      if (this.website) {
        return this.website.startsWith('http') ? this.website : `https://${this.website}`
      }
      return 'https://www.facebook.com'
    }
  },

  methods: {
    openFacebookPage() {
      window.open(this.targetPageUrl, '_blank')
    },

    openWebsite() {
      if (this.website) {
        const target = this.website.startsWith('http') ? this.website : `https://${this.website}`
        window.open(target, '_blank')
      }
    },

    callPhone() {
      if (this.phone) {
        window.location.href = `tel:${this.phone}`
      }
    },

    sharePage() {
      const shareUrl = this.targetPageUrl || window.location.href
      if (navigator.share) {
        navigator.share({ title: this.name, url: shareUrl }).catch(() => {})
      } else {
        window.open(shareUrl, '_blank')
      }
    }
  }
}
</script>

<style scoped>
.fmp-device {
  background: #e8e8e8;
  border-radius: 36px;
  padding: 14px 10px;
  width: 260px;
  flex-shrink: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), inset 0 0 0 2px #dadada;
  margin: 0 auto;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  user-select: none;
}

/* ─── Facebook Header bar ─── */
.fmp-header-bar {
  height: 42px;
  background: #1877f2;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  box-sizing: border-box;
}

.fmp-search-box {
  flex: 1;
  height: 28px;
  background: rgba(255, 255, 255, 0.22);
  border-radius: 14px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 6px;
  color: #fff;
  min-width: 0;
  cursor: pointer;
  transition: background-color 0.15s;
}

.fmp-search-box:hover {
  background: rgba(255, 255, 255, 0.32);
}

.fmp-search-icon {
  font-size: 16px;
  opacity: 0.9;
  flex-shrink: 0;
}

.fmp-search-text {
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.95;
}

.fmp-header-icon {
  font-size: 18px;
  cursor: pointer;
  opacity: 0.9;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.fmp-header-icon:hover {
  opacity: 1;
}

.fmp-clickable {
  cursor: pointer;
}

/* ─── Screen Content ─── */
.fmp-screen {
  background: #ffffff;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

/* ─── Cover Banner ─── */
.fmp-cover {
  height: 90px;
  background-color: #050505;
  background-size: cover;
  background-position: center;
  position: relative;
}

.fmp-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #444;
}

.fmp-cover-placeholder i {
  font-size: 32px;
}

/* ─── Profile Info ─── */
.fmp-profile-info {
  padding: 0 12px 14px;
  position: relative;
  box-sizing: border-box;
}

.fmp-avatar-wrapper {
  margin-top: -32px;
  margin-bottom: 8px;
  display: inline-block;
}

.fmp-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 3px solid #fff;
  background-color: #e4e6eb;
  background-size: cover;
  background-position: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.fmp-avatar-placeholder {
  font-size: 32px;
  color: #8a8d91;
}

.fmp-identity {
  margin-bottom: 14px;
}

.fmp-page-name {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 700;
  color: #050505;
  line-height: 1.2;
  cursor: pointer;
  transition: color 0.15s;
}

.fmp-page-name:hover {
  color: #1877f2;
}

.fmp-page-category {
  margin: 0;
  font-size: 11px;
  color: #65676b;
}

/* ─── Details List ─── */
.fmp-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid #ced0d4;
  padding-top: 12px;
}

.fmp-detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #050505;
  word-break: break-word;
}

.fmp-detail-item--clickable {
  cursor: pointer;
}

.fmp-detail-item i {
  font-size: 15px;
  color: #65676b;
  flex-shrink: 0;
}

.fmp-status-open {
  color: #31a24c;
  font-weight: 600;
}

.fmp-link {
  color: #1877f2;
}

.fmp-link:hover {
  text-decoration: underline;
}
</style>
